import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { api, ApiError } from '../lib/api'
import { getTeamToken, setTeamToken } from '../lib/storage'
import type { ClaimResponse, DraftResponse, DraftState } from '../types/protocol'

export default function Join() {
  const { code = '' } = useParams()
  const joinCode = code.toUpperCase()
  const navigate = useNavigate()
  const [draft, setDraft] = useState<DraftState | null>(null)
  const [notFound, setNotFound] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [ownerName, setOwnerName] = useState('')
  const [claimingId, setClaimingId] = useState<number | null>(null)
  const alreadyJoined = getTeamToken(joinCode) !== null

  const refetch = useCallback(async () => {
    try {
      const response = await api<DraftResponse>(`/drafts/${joinCode}`)
      setDraft(response.draft)
      setNotFound(false)
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) setNotFound(true)
      else setError('Could not load the draft. Is the server up?')
    }
  }, [joinCode])

  // Claimed badges refresh on refetch only until Phase 6 realtime.
  useEffect(() => {
    void refetch()
    const onFocus = () => void refetch()
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [refetch])

  async function claim(teamId: number) {
    setClaimingId(teamId)
    setError(null)
    try {
      const response = await api<ClaimResponse>(`/drafts/${joinCode}/teams/${teamId}/claim`, {
        method: 'POST',
        body: { owner_name: ownerName.trim() || undefined },
      })
      setTeamToken(joinCode, response.team_token)
      navigate(`/draft/${joinCode}`)
    } catch (e) {
      if (e instanceof ApiError && e.code === 'team_claimed') {
        setError('That team was just claimed — pick another one.')
        void refetch()
      } else if (e instanceof ApiError && e.code === 'rate_limited') {
        setError('Too many attempts — wait a minute and try again.')
      } else {
        setError(e instanceof ApiError ? e.message : 'Could not claim the team')
      }
    } finally {
      setClaimingId(null)
    }
  }

  if (notFound) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center px-4 text-center">
        <p className="eyebrow">No such draft</p>
        <h1 className="mt-3 font-display text-4xl font-bold text-ink-hi">
          Nothing joins with{' '}
          <span className="font-mono text-3xl tracking-[0.15em] text-signal">{joinCode}</span>
        </h1>
        <p className="mt-3 text-ink-lo">Double-check the code on the board.</p>
        <Link
          to="/"
          className="mt-6 text-sm text-ink-mid underline-offset-4 hover:text-ink-hi hover:underline"
        >
          Back to start
        </Link>
      </main>
    )
  }

  if (!draft) {
    return (
      <main className="flex min-h-screen items-center justify-center text-sm text-ink-lo">
        {error ?? 'Loading…'}
      </main>
    )
  }

  return (
    <main className="min-h-screen">
      <div className="mx-auto max-w-xl px-4 py-10">
        <p className="eyebrow">Claim your team</p>
        <h1 className="mt-2 font-display text-4xl leading-none font-bold text-ink-hi">
          {draft.name}
        </h1>
        <p className="mt-2 text-sm text-ink-lo">
          Pick the team that&apos;s yours. Everyone else sees it taken the moment you do.
        </p>

        {alreadyJoined && (
          <div className="mt-5 flex items-center justify-between gap-3 rounded-md border border-turn/40 bg-turn/10 p-3 text-sm shadow-raised">
            <span className="text-turn">This device already claimed a team.</span>
            <Link
              to={`/draft/${joinCode}`}
              className="shrink-0 rounded-sm bg-turn px-3 py-1.5 font-semibold text-ink shadow-raised hover:brightness-110"
            >
              Go to my draft room
            </Link>
          </div>
        )}

        <label className="mt-6 flex flex-col gap-1.5">
          <span className="eyebrow">Your name (shown on the board)</span>
          <input
            value={ownerName}
            onChange={(event) => setOwnerName(event.target.value)}
            placeholder="Optional"
            className="rounded-md border border-line bg-surface-3 px-3 py-2.5 text-sm text-ink-hi shadow-[inset_0_1px_2px_rgb(0_0_0/0.4)] placeholder:text-ink-lo focus:border-signal/60 focus:outline-none"
          />
        </label>

        {/* Taken and claimable rows are visually different weights, not the same
            row with a different badge: a claimed team is no longer a choice, so
            it recedes a full elevation step instead of staying a peer. */}
        <ul className="mt-6 space-y-2">
          {draft.teams.map((team) => (
            <li
              key={team.id}
              className={`flex items-center gap-3 rounded-md border p-3 ${
                team.claimed
                  ? 'border-line/60 bg-ink/40'
                  : 'border-line bg-surface-2 shadow-raised'
              }`}
            >
              <span
                className={`w-6 shrink-0 text-center font-mono text-xs tabular-nums ${
                  team.claimed ? 'text-ink-lo' : 'text-signal'
                }`}
              >
                {team.draft_position ?? '—'}
              </span>
              <div className="min-w-0 flex-1">
                <p
                  className={`truncate font-display text-lg leading-tight font-semibold ${
                    team.claimed ? 'text-ink-lo' : 'text-ink-hi'
                  }`}
                >
                  {team.name}
                </p>
                {team.owner_name && (
                  <p className="truncate text-xs text-ink-lo">{team.owner_name}</p>
                )}
              </div>
              {team.claimed ? (
                <span className="rounded-full border border-line px-3 py-1 font-mono text-[10px] tracking-wider text-ink-lo uppercase">
                  Taken
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => void claim(team.id)}
                  disabled={claimingId !== null}
                  className="rounded-sm bg-signal px-4 py-1.5 text-sm font-bold text-ink shadow-raised hover:bg-signal-dim disabled:opacity-50"
                >
                  {claimingId === team.id ? 'Claiming…' : 'Claim'}
                </button>
              )}
            </li>
          ))}
        </ul>

        {draft.teams.length === 0 && (
          <p className="mt-4 text-sm text-ink-lo">
            The commissioner hasn&apos;t added any teams yet — check back in a moment.
          </p>
        )}

        {error && <p className="mt-4 text-sm text-crit">{error}</p>}

        <button
          type="button"
          onClick={() => void refetch()}
          className="mt-6 text-sm text-ink-lo underline-offset-4 hover:text-ink-hi hover:underline"
        >
          Refresh list
        </button>
      </div>
    </main>
  )
}
